'use client'

import { useState, useEffect } from 'react'
import { Settings, Users, Sparkles, ChevronDown } from 'lucide-react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import MembersManager from '@/components/workspace/members-manager'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslations } from 'next-intl'

interface WorkspaceSettingsModalProps {
  workspaceId: string
  workspaceName: string
  defaultTab?: 'general' | 'members'
}

export default function WorkspaceSettingsModal({
  workspaceId: initialWorkspaceId,
  workspaceName: initialWorkspaceName,
  defaultTab = 'members',
}: WorkspaceSettingsModalProps) {
  const t = useTranslations('modals.workspace_settings')
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [currentUserRole, setCurrentUserRole] = useState<string>('viewer')
  const [currentUserIsOwner, setCurrentUserIsOwner] = useState(false)
  const [loading, setLoading] = useState(true)

  // Workspace switching
  const { workspaces } = useWorkspaces()
  const [selectedWorkspaceId, setSelectedWorkspaceId] =
    useState(initialWorkspaceId)
  const selectedWorkspace = workspaces.find(
    (w: any) => w._id.toString() === selectedWorkspaceId,
  )
  const currentWorkspaceName = selectedWorkspace?.name || initialWorkspaceName

  useEffect(() => {
    // Fetch current user's role in this workspace
    const fetchRole = async () => {
      setLoading(true)
      try {
        const res = await fetch(
          `/api/me/workspaces/${selectedWorkspaceId}/members`,
        )
        const data = await res.json()
        if (data.success) {
          setCurrentUserRole(data.data.current_user_role || 'viewer')
          setCurrentUserIsOwner(data.data.current_user_is_owner || false)
        }
      } catch (error) {
        console.error('Failed to fetch user role:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchRole()
  }, [selectedWorkspaceId])

  return (
    <DialogContent
      className={cn(
        'sm:max-w-[640px] max-w-[95vw] max-h-[85vh]',
        'bg-[#0f0f0f] border border-gold/20',
        'shadow-[0_0_60px_rgba(255,215,0,0.1)]',
        'backdrop-blur-xl',
        'p-0 gap-0 overflow-hidden',
        'rounded-2xl',
      )}
      overlayClassName="bg-black/80 backdrop-blur-sm"
    >
      {/* Header */}
      <DialogHeader
        className={cn(
          'px-6 pt-6 pb-4',
          'border-b border-gold/10',
          'bg-gradient-to-b from-gold/5 to-transparent',
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-10 h-10 rounded-xl',
              'bg-gold/10 border border-gold/20',
              'flex items-center justify-center',
            )}
          >
            <Settings className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1">
            {workspaces.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-lg font-bold text-white hover:text-gold transition-colors"
                  >
                    {currentWorkspaceName}
                    <ChevronDown className="w-4 h-4 text-white/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="bg-[#1a1a1a] border-gold/20 max-h-[40vh] overflow-y-auto overscroll-contain"
                >
                  {workspaces.map((ws: any) => (
                    <DropdownMenuItem
                      key={ws._id.toString()}
                      onClick={() => setSelectedWorkspaceId(ws._id.toString())}
                      className={cn(
                        'cursor-pointer',
                        ws._id.toString() === selectedWorkspaceId &&
                          'bg-gold/10 text-gold',
                      )}
                    >
                      {ws.name || `Workspace ${ws._id.toString().slice(-6)}`}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <DialogTitle className="text-lg font-bold text-white">
                {currentWorkspaceName}
              </DialogTitle>
            )}
            <p className="text-sm text-white/50">{t('title')}</p>
          </div>
        </div>
      </DialogHeader>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as any)}
        className="flex flex-col h-[calc(85vh-100px)]"
      >
        <TabsList
          className={cn(
            'w-full justify-start px-6 py-0',
            'bg-transparent border-b border-gold/10',
            'rounded-none h-auto',
          )}
        >
          <TabsTrigger
            value="members"
            className={cn(
              'relative py-3 px-4 rounded-none',
              'data-[state=active]:bg-transparent',
              'data-[state=active]:text-gold',
              'text-white/50 hover:text-white/70',
              'transition-colors duration-200',
              // Gold underline for active state
              'after:absolute after:bottom-0 after:left-0 after:right-0',
              'after:h-0.5 after:bg-gold',
              'after:scale-x-0 data-[state=active]:after:scale-x-100',
              'after:transition-transform after:duration-200',
            )}
          >
            <Users className="w-4 h-4 mr-2" />
            {t('members_tab')}
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="members" className="mt-0 p-6 h-full">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
                  <Sparkles className="w-5 h-5 text-gold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="text-white/50 text-sm">{t('loading')}</p>
              </div>
            ) : (
              <MembersManager
                workspaceId={selectedWorkspaceId}
                workspaceName={currentWorkspaceName}
                currentUserRole={currentUserRole}
                currentUserIsOwner={currentUserIsOwner}
              />
            )}
          </TabsContent>
        </div>
      </Tabs>
    </DialogContent>
  )
}
